import { TaJson } from "ta-json";
import URI from "urijs";
import URITemplate from "urijs/src/URITemplate";

import { MapStringTo } from "../base-types";
import { HEADERS, QUERY, SEARCH_AFTER } from "../constants/api";
import { Query } from "../contracts/querying/query";
import { IQueryLoadConfiguration } from "../contracts/querying/query-load-configuration";
import { QueryRequest } from "../contracts/querying/query-request";
import { QuerySortOrder } from "../contracts/querying/query-sort-order";
import { ScrollRequest } from "../contracts/querying/scroll-request";
import { SearchAfterQuery } from "../contracts/querying/search-after-query";
import { SortFieldType } from "../contracts/querying/sort-field-type";
import { Sorting } from "../contracts/querying/sorting";
import Guard from "../guard";
import { EntityCollectionResource } from "../models/entity-collection-resource";
import { QueryResultResource } from "../models/query-result-resource";
import { IExtendedContentHubClient } from "./extended-client";
import { CancelCallback } from "./internal-client";
import { ResponseHandler } from "./response-handler";

export interface ISchemaQuerying {
    queryWithSchemaAsync(
        query: Query,
        loadConfiguration: IQueryLoadConfiguration,
        cancelCallback?: CancelCallback
    ): Promise<QueryResultResource>;

    scrollWithSchemaAsync(scrollRequest: ScrollRequest, cancelCallback?: CancelCallback): Promise<QueryResultResource>;

    searchAfterWithSchemaAsync(
        query: SearchAfterQuery,
        cancelCallback?: CancelCallback
    ): Promise<EntityCollectionResource>;
}

export class SchemaQuerying implements ISchemaQuerying {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client);
        this._client = client;
    }

    async queryWithSchemaAsync(
        query: Query,
        loadConfiguration: IQueryLoadConfiguration,
        cancelCallback?: CancelCallback
    ): Promise<QueryResultResource> {
        Guard.notNullOrUndefined(query, "query");
        Guard.notNullOrUndefined(loadConfiguration, "loadConfiguration");
        //TODO query.validate();

        const routes = await this._client.api.getApiRoutesAsync();
        const queryEndpoint = routes[QUERY.templateName];

        let uri: URI;
        /* istanbul ignore next */
        if (queryEndpoint.templated) {
            const uriTemplate = new URITemplate(queryEndpoint.href);
            uri = uriTemplate.expand({});
        } else {
            uri = new URI(queryEndpoint.href);
        }

        const queryRequest = new QueryRequest({
            query: query,
            loadConfiguration: loadConfiguration,
        });

        const content = TaJson.serialize(queryRequest);

        const resource = await this.queryEndpointAsync(uri, content, cancelCallback);

        return resource;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/require-await
    async scrollWithSchemaAsync(scrollRequest: ScrollRequest): Promise<QueryResultResource> {
        throw new Error("Method not implemented."); //TODO
    }

    async searchAfterWithSchemaAsync(
        query: SearchAfterQuery,
        cancelCallback?: CancelCallback
    ): Promise<EntityCollectionResource> {
        Guard.notNullOrUndefined(query, "query");
        Guard.notNullOrUndefined(query.filter, "query.filter");

        const routes = await this._client.api.getApiRoutesAsync();
        const queryEndpoint = routes[SEARCH_AFTER.templateName];

        let uri: URI;
        /* istanbul ignore next */
        if (queryEndpoint.templated) {
            const uriTemplate = new URITemplate(queryEndpoint.href);
            uri = uriTemplate.expand({});
        } else {
            uri = new URI(queryEndpoint.href);
        }

        if (query.sorting == null || query.sorting.length == 0) {
            query.sorting = [
                new Sorting({ field: "created_on", fieldType: SortFieldType.Property, order: QuerySortOrder.Desc }),
            ];
        }

        const headers: MapStringTo<string> = {};
        headers[HEADERS.apiVersion] = "2";
        headers[HEADERS.minimalSchema] = "true";
        headers["Content-Type"] = "application/json";

        const content = TaJson.serialize(query);
        const response = await this._client.raw.postAsync(uri, content, headers, cancelCallback);

        ResponseHandler.handleErrors(response);
        const resource = TaJson.deserialize<EntityCollectionResource>(response.content, EntityCollectionResource);

        return resource;
    }

    private async queryEndpointAsync(
        endpoint: string | URI,
        content: unknown,
        cancelCallback?: CancelCallback
    ): Promise<QueryResultResource> {
        const response = await this._client.raw.postAsync(
            endpoint,
            content,
            {
                [HEADERS.apiVersion]: "3",
                [HEADERS.minimalSchema]: "true",
                "Content-Type": "application/json",
            },
            cancelCallback
        );

        ResponseHandler.handleErrors(response);

        const resource = TaJson.deserialize<QueryResultResource>(response.content, QueryResultResource);

        return resource;
    }
}
